'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';
import useScrollReveal from '@/hooks/useScrollReveal';
import { listBucketMedia, StorageMediaItem, PortfolioBucket } from '@/lib/portfolioStorage';
import { useAuth } from '@/contexts/AuthContext';
import AdminUploadPanel from './AdminUploadPanel';
import AdminLoginModal from './AdminLoginModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ShowcaseCategory {
  id: PortfolioBucket;
  title: string;
  description: string;
  icon: string;
  accentColor: string;
  placeholderType: 'image' | 'video' | 'link';
  placeholderLabel: string;
}

interface YouTubeEntry {
  id: string;
  url: string;
  title: string;
  videoId: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const YOUTUBE_STORAGE_KEY = 'lonestar_youtube_links';

const categories: ShowcaseCategory[] = [
  {
    id: 'logos-brand-identity',
    title: 'Logos & Brand Identity',
    description: 'Visual identity systems, logo design, and brand strategy for Dallas-based businesses and nonprofits.',
    icon: 'SparklesIcon',
    accentColor: 'brand-teal',
    placeholderType: 'image',
    placeholderLabel: 'Logo / Brand Identity',
  },
  {
    id: 'content-creation',
    title: 'Content Creation',
    description: 'Full-scale video production, motion graphics, drone footage, and social media content crafted for clients across Texas and beyond.',
    icon: 'FilmIcon',
    accentColor: 'brand-orange',
    placeholderType: 'video',
    placeholderLabel: 'Video or Graphic',
  },
  {
    id: 'websites-seo',
    title: 'Websites & SEO',
    description: 'Custom websites, SEO strategies, and digital infrastructure built for real business growth.',
    icon: 'GlobeAltIcon',
    accentColor: 'brand-green',
    placeholderType: 'link',
    placeholderLabel: 'Website Screenshot or SEO Results',
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function getYouTubeThumbnail(videoId: string): string {
  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
}

// ─── Placeholder Slot ─────────────────────────────────────────────────────────

function PlaceholderSlot({ label, type, accentColor }: { label: string; type: string; accentColor: string }) {
  const iconName = type === 'video' ? 'PlayCircleIcon' : type === 'link' ? 'ComputerDesktopIcon' : 'PhotoIcon';
  return (
    <div className="group relative aspect-video bg-brand-offwhite border-2 border-dashed border-gray-300 hover:border-brand-teal rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg flex flex-col items-center justify-center p-4 cursor-default">
      <div className={`w-12 h-12 rounded-full bg-${accentColor}/10 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}>
        <Icon name={iconName} size={24} className={`text-${accentColor}`} />
      </div>
      <p className="text-xs text-text-secondary font-body text-center leading-relaxed max-w-[160px]">
        {label} — coming soon
      </p>
    </div>
  );
}

// ─── Image Lightbox ───────────────────────────────────────────────────────────

function ImageLightbox({ src, alt, onClose }: { src: string; alt: string; onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      style={{ padding: '48px 24px' }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
        aria-label="Close"
      >
        <Icon name="XMarkIcon" size={22} />
      </button>
      <img
        src={src}
        alt={alt}
        style={{
          maxWidth: '90vw',
          maxHeight: 'calc(100vh - 96px)',
          width: 'auto',
          height: 'auto',
          objectFit: 'contain',
          display: 'block',
        }}
        className="rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
}

// ─── YouTube Lightbox ─────────────────────────────────────────────────────────

function YouTubeLightbox({ videoId, title, onClose }: { videoId: string; title: string; onClose: () => void }) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-10"
        aria-label="Close"
      >
        <Icon name="XMarkIcon" size={22} />
      </button>
      <div
        className="w-full max-w-4xl aspect-video rounded-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    </div>
  );
}

// ─── YouTube Card ─────────────────────────────────────────────────────────────

function YouTubeCard({
  entry,
  onPlay,
  onDelete,
  isAdmin,
}: {
  entry: YouTubeEntry;
  onPlay: () => void;
  onDelete?: () => void;
  isAdmin: boolean;
}) {
  return (
    <div className="relative group aspect-video rounded-lg overflow-hidden shadow-card bg-black cursor-pointer" onClick={onPlay}>
      <img
        src={getYouTubeThumbnail(entry.videoId)}
        alt={entry.title || 'YouTube video'}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      {/* Play overlay */}
      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors duration-300">
        <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
          <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      {/* Title bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-3">
        <p className="text-white text-xs font-body font-medium truncate">{entry.title || 'YouTube Video'}</p>
      </div>
      {/* Admin delete */}
      {isAdmin && onDelete && (
        <button
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="absolute top-2 right-2 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
          title="Remove video"
        >
          <Icon name="TrashIcon" size={13} />
        </button>
      )}
    </div>
  );
}

// ─── YouTube Admin Panel ──────────────────────────────────────────────────────

function YouTubeAdminPanel({
  entries,
  onAdd,
  onDelete,
}: {
  entries: YouTubeEntry[];
  onAdd: (entry: YouTubeEntry) => void;
  onDelete: (id: string) => void;
}) {
  const [inputUrl, setInputUrl] = useState('');
  const [inputTitle, setInputTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleAdd = () => {
    setError(null);
    const videoId = extractYouTubeId(inputUrl.trim());
    if (!videoId) {
      setError('Please enter a valid YouTube URL (youtube.com/watch?v=... or youtu.be/...)');
      return;
    }
    const newEntry: YouTubeEntry = {
      id: `yt_${Date.now()}`,
      url: inputUrl.trim(),
      title: inputTitle.trim() || 'YouTube Video',
      videoId,
    };
    onAdd(newEntry);
    setInputUrl('');
    setInputTitle('');
  };

  return (
    <div className="mt-6 border border-brand-orange/30 rounded-lg overflow-hidden">
      <div className="bg-brand-orange/5 border-b border-brand-orange/20 px-4 py-3 flex items-center gap-2">
        <Icon name="ShieldCheckIcon" size={16} className="text-brand-orange" />
        <span className="font-body font-semibold text-brand-plum text-sm">Admin — Add YouTube Videos</span>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className="block font-body text-xs font-semibold text-brand-plum mb-1">YouTube URL *</label>
          <input
            type="url"
            value={inputUrl}
            onChange={(e) => setInputUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-body text-sm text-brand-plum placeholder-gray-400 focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal"
          />
        </div>
        <div>
          <label className="block font-body text-xs font-semibold text-brand-plum mb-1">Title (optional)</label>
          <input
            type="text"
            value={inputTitle}
            onChange={(e) => setInputTitle(e.target.value)}
            placeholder="Video title..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-body text-sm text-brand-plum placeholder-gray-400 focus:outline-none focus:border-brand-teal focus:ring-1 focus:ring-brand-teal"
          />
        </div>
        {error && (
          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
            <Icon name="ExclamationCircleIcon" size={14} className="text-red-500 flex-shrink-0" />
            <p className="font-body text-xs text-red-600">{error}</p>
          </div>
        )}
        <button
          onClick={handleAdd}
          className="flex items-center gap-2 px-4 py-2 bg-brand-orange text-white font-body font-semibold text-sm rounded-lg hover:bg-brand-orange/90 transition-colors"
        >
          <Icon name="PlusIcon" size={16} />
          Add Video
        </button>
      </div>
      {entries.length > 0 && (
        <div className="border-t border-brand-orange/20 px-4 py-3">
          <p className="font-body text-xs text-text-secondary font-semibold mb-2">{entries.length} video{entries.length !== 1 ? 's' : ''} added</p>
          <div className="space-y-1.5">
            {entries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0">
                  <img src={getYouTubeThumbnail(entry.videoId)} alt="" className="w-full h-full object-cover" />
                </div>
                <span className="font-body text-xs text-brand-plum flex-1 truncate">{entry.title}</span>
                <button
                  onClick={() => onDelete(entry.id)}
                  className="w-6 h-6 flex items-center justify-center text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                  title="Remove"
                >
                  <Icon name="TrashIcon" size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Image Media Slot ─────────────────────────────────────────────────────────

function ImageMediaSlot({ item, onImageClick }: { item: StorageMediaItem; onImageClick: (url: string, alt: string) => void }) {
  return (
    <div
      className="relative aspect-video rounded-lg overflow-hidden shadow-card group cursor-zoom-in"
      onClick={() => onImageClick(item.url, item.name.replace(/^\d+_/, ''))}
    >
      <img
        src={item.url}
        alt={item.name.replace(/^\d+_/, '')}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors duration-300">
        <div className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow">
          <Icon name="MagnifyingGlassPlusIcon" size={18} className="text-brand-plum" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs font-body px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 truncate">
        {item.name.replace(/^\d+_/, '')}
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MediaShowcase() {
  const [activeTab, setActiveTab] = useState<PortfolioBucket>('logos-brand-identity');
  const [mediaByBucket, setMediaByBucket] = useState<Record<PortfolioBucket, StorageMediaItem[]>>({
    'logos-brand-identity': [],
    'content-creation': [],
    'websites-seo': [],
  });
  const [loadingBuckets, setLoadingBuckets] = useState<Record<PortfolioBucket, boolean>>({
    'logos-brand-identity': true,
    'content-creation': true,
    'websites-seo': true,
  });
  const [showLoginModal, setShowLoginModal] = useState(false);

  // YouTube state
  const [youtubeEntries, setYoutubeEntries] = useState<YouTubeEntry[]>([]);
  const [activeLightboxVideo, setActiveLightboxVideo] = useState<YouTubeEntry | null>(null);

  // Image lightbox state
  const [activeLightboxImage, setActiveLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  const { ref: sectionRef, isVisible: sectionVisible } = useScrollReveal<HTMLElement>();
  const { user, signOut } = useAuth();

  const loadBucket = useCallback(async (bucket: PortfolioBucket) => {
    setLoadingBuckets((prev) => ({ ...prev, [bucket]: true }));
    const items = await listBucketMedia(bucket);
    setMediaByBucket((prev) => ({ ...prev, [bucket]: items }));
    setLoadingBuckets((prev) => ({ ...prev, [bucket]: false }));
  }, []);

  useEffect(() => {
    categories.forEach((cat) => loadBucket(cat.id));
  }, [loadBucket]);

  // Load YouTube entries from localStorage after mount (client-only)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(YOUTUBE_STORAGE_KEY);
      if (stored) setYoutubeEntries(JSON.parse(stored));
    } catch {
      // ignore
    }
  }, []);

  // Persist YouTube entries to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(YOUTUBE_STORAGE_KEY, JSON.stringify(youtubeEntries));
    }
  }, [youtubeEntries]);

  const handleUploadComplete = useCallback((item: StorageMediaItem) => {
    setMediaByBucket((prev) => ({
      ...prev,
      [item.bucket]: [item, ...prev[item.bucket]],
    }));
  }, []);

  const handleDeleteComplete = useCallback((bucket: PortfolioBucket, itemId: string) => {
    setMediaByBucket((prev) => ({
      ...prev,
      [bucket]: prev[bucket].filter((i) => i.id !== itemId),
    }));
  }, []);

  const handleAddYouTube = useCallback((entry: YouTubeEntry) => {
    setYoutubeEntries((prev) => [entry, ...prev]);
  }, []);

  const handleDeleteYouTube = useCallback((id: string) => {
    setYoutubeEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const activeCategory = categories.find((c) => c.id === activeTab) || categories[0];
  const activeItems = mediaByBucket[activeTab];
  const isLoading = loadingBuckets[activeTab];
  const PLACEHOLDER_COUNT = 6;

  // Determine tab badge count
  const getTabCount = (catId: PortfolioBucket) => {
    if (catId === 'content-creation') return youtubeEntries.length;
    return mediaByBucket[catId].length;
  };

  return (
    <section
      ref={sectionRef}
      className={`py-16 md:py-24 bg-white transition-all duration-1000 ${sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-10 md:mb-14">
          <div className="inline-block px-4 py-1.5 bg-brand-teal text-white font-body text-xs font-bold uppercase tracking-wider mb-4">
            OUR CREATIVE WORK
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl text-brand-plum font-bold leading-tight mb-4">
            What We Make
          </h2>
          <p className="font-body text-base md:text-lg text-text-secondary max-w-2xl leading-relaxed">
            From bold brand identities and full-scale video production to custom website builds and graphic design — this is the creative and technical work we deliver for businesses across Texas and beyond.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 md:gap-3 mb-10 border-b border-gray-200 pb-0">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveTab(cat.id)}
              className={`flex items-center gap-2 px-4 md:px-6 py-3 font-body font-semibold text-sm transition-all duration-300 border-b-2 -mb-px ${
                activeTab === cat.id
                  ? `border-brand-teal text-brand-teal bg-brand-teal/5`
                  : 'border-transparent text-text-secondary hover:text-brand-plum hover:border-gray-300'
              }`}
            >
              <Icon name={cat.icon} size={18} />
              <span>{cat.title}</span>
              {getTabCount(cat.id) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-brand-teal/10 text-brand-teal text-xs rounded-full font-bold">
                  {getTabCount(cat.id)}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Active Category */}
        <div>
          <div className="mb-6">
            <p className="font-body text-text-secondary text-base leading-relaxed max-w-2xl">
              {activeCategory.description}
            </p>
          </div>

          {/* ── Content Creation Tab: YouTube Grid ── */}
          {activeTab === 'content-creation' ? (
            <>
              {youtubeEntries.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {youtubeEntries.map((entry) => (
                    <YouTubeCard
                      key={entry.id}
                      entry={entry}
                      onPlay={() => setActiveLightboxVideo(entry)}
                      onDelete={() => handleDeleteYouTube(entry.id)}
                      isAdmin={!!user}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
                    <PlaceholderSlot
                      key={i}
                      label="YouTube Video"
                      type="video"
                      accentColor="brand-orange"
                    />
                  ))}
                </div>
              )}

              {/* Admin YouTube panel */}
              {user && (
                <YouTubeAdminPanel
                  entries={youtubeEntries}
                  onAdd={handleAddYouTube}
                  onDelete={handleDeleteYouTube}
                />
              )}
            </>
          ) : (
            /* ── Image Tabs: Logos & Websites ── */
            <>
              {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
                    <div key={i} className="aspect-video bg-gray-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : activeItems.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {activeItems.map((item) => (
                    <ImageMediaSlot
                      key={item.id}
                      item={item}
                      onImageClick={(src, alt) => setActiveLightboxImage({ src, alt })}
                    />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {Array.from({ length: PLACEHOLDER_COUNT }).map((_, i) => (
                    <PlaceholderSlot
                      key={i}
                      label={activeCategory.placeholderLabel}
                      type={activeCategory.placeholderType}
                      accentColor={activeCategory.accentColor}
                    />
                  ))}
                </div>
              )}

              {/* Admin Upload Panel (only visible when logged in, not for content-creation) */}
              {user && (
                <AdminUploadPanel
                  bucket={activeTab}
                  categoryLabel={activeCategory.title}
                  items={activeItems}
                  onUploadComplete={handleUploadComplete}
                  onDeleteComplete={(itemId) => handleDeleteComplete(activeTab, itemId)}
                />
              )}
            </>
          )}

          {/* Admin bar when logged in */}
          {user && (
            <div className="mt-4 flex items-center justify-between px-4 py-2.5 bg-brand-plum/5 border border-brand-plum/20 rounded-lg">
              <div className="flex items-center gap-2">
                <Icon name="ShieldCheckIcon" size={15} className="text-brand-teal" />
                <span className="font-body text-xs text-brand-plum font-semibold">
                  Logged in as admin
                </span>
                <span className="font-body text-xs text-text-secondary truncate max-w-[180px]">
                  — {user.email}
                </span>
              </div>
              <button
                onClick={() => signOut()}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-body font-semibold text-brand-plum/70 hover:text-brand-plum border border-brand-plum/20 hover:border-brand-plum/40 rounded-lg transition-colors"
              >
                <Icon name="ArrowRightOnRectangleIcon" size={13} />
                Sign out
              </button>
            </div>
          )}

          {/* Public CTA / Admin login trigger (only visible when not logged in) */}
          {!user && (
            <div className="mt-8 p-5 bg-brand-offwhite border border-dashed border-brand-teal/40 rounded-lg flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 bg-brand-teal/10 rounded-lg flex-shrink-0">
                <Icon name="ArrowUpTrayIcon" size={20} className="text-brand-teal" />
              </div>
              <div className="flex-1">
                <p className="font-body font-semibold text-brand-plum text-sm mb-0.5">Ready to see our work?</p>
                <p className="font-body text-text-secondary text-sm">
                  Send your project inquiries to{' '}
                  <a href="mailto:hello@lonestarlocal.com" className="text-brand-teal hover:underline font-medium">
                    hello@lonestarlocal.com
                  </a>{' '}
                  and let's build something great together.
                </p>
              </div>
              <button
                onClick={() => setShowLoginModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-body font-semibold text-brand-plum/50 hover:text-brand-plum border border-brand-plum/20 hover:border-brand-plum/40 rounded-lg transition-colors flex-shrink-0"
                title="Admin login"
              >
                <Icon name="LockClosedIcon" size={13} />
                Admin
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Admin Login Modal */}
      {showLoginModal && (
        <AdminLoginModal onClose={() => setShowLoginModal(false)} />
      )}

      {/* YouTube Lightbox */}
      {activeLightboxVideo && (
        <YouTubeLightbox
          videoId={activeLightboxVideo.videoId}
          title={activeLightboxVideo.title}
          onClose={() => setActiveLightboxVideo(null)}
        />
      )}

      {/* Image Lightbox */}
      {activeLightboxImage && (
        <ImageLightbox
          src={activeLightboxImage.src}
          alt={activeLightboxImage.alt}
          onClose={() => setActiveLightboxImage(null)}
        />
      )}
    </section>
  );
}
